import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { workOrdersApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  FileText, 
  ArrowLeft, 
  Calendar, 
  Printer,
  Users,
  FlaskConical,
  RefreshCw,
  Loader2,
  Search
} from "lucide-react";

export default function WorkOrder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().split('T')[0];
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchOrders();
  }, [selectedDate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await workOrdersApi.getAll(null, selectedDate);
      // Sort by folio
      const sortedOrders = response.data.sort((a, b) => {
        const folioA = a.patient_folio || a.order_number || '';
        const folioB = b.patient_folio || b.order_number || '';
        return folioA.localeCompare(folioB);
      });
      setOrders(sortedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Error al cargar las órdenes");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter orders based on search term (by study name or code)
  const filteredOrders = searchTerm.trim() 
    ? orders.filter(order => 
        order.studies?.some(study => 
          study.study_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          study.study_code?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : orders;

  // Calculate study statistics for the search result
  const getStudyStats = () => {
    if (!searchTerm.trim()) return null;
    
    const studyCount = {};
    orders.forEach(order => {
      order.studies?.forEach(study => {
        const studyName = study.study_name || 'Sin nombre';
        const matchesSearch = studyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (study.study_code || '').toLowerCase().includes(searchTerm.toLowerCase());
        if (matchesSearch) {
          if (!studyCount[studyName]) {
            studyCount[studyName] = { count: 0, code: study.study_code };
          }
          studyCount[studyName].count++;
        }
      });
    });
    
    return Object.entries(studyCount).map(([name, data]) => ({
      name,
      code: data.code,
      patientCount: data.count
    }));
  };

  const studyStats = getStudyStats();

  // Calculate totals
  const totalPatients = filteredOrders.length;
  const totalStudies = filteredOrders.reduce((acc, order) => acc + (order.studies?.length || 0), 0);

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="work-order-page">
      {/* Header - hide on print */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
              Orden de Trabajo del Día
            </h1>
            <p className="text-muted-foreground">
              Lista de pacientes y estudios registrados
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* Date selector - hide on print */}
      <Card className="print:hidden">
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="selectedDate" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Seleccionar Fecha
              </Label>
              <Input
                id="selectedDate"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-48"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print Header - only show on print */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-xl font-bold">ORDEN DE TRABAJO DIARIA</h1>
        <p className="text-sm text-gray-600">{formatDate(selectedDate)}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 print:gap-2">
        <Card className="print:border print:shadow-none">
          <CardContent className="pt-6 print:pt-3 print:pb-3">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full print:p-2">
                <Users className="h-6 w-6 text-blue-600 print:h-4 print:w-4" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Pacientes</p>
                <p className="text-3xl font-bold print:text-xl">{totalPatients}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="print:border print:shadow-none">
          <CardContent className="pt-6 print:pt-3 print:pb-3">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full print:p-2">
                <FlaskConical className="h-6 w-6 text-green-600 print:h-4 print:w-4" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Estudios</p>
                <p className="text-3xl font-bold print:text-xl">{totalStudies}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card className="print:border print:shadow-none">
        <CardHeader className="print:py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 print:text-base">
              <FileText className="h-5 w-5 print:h-4 print:w-4" />
              Detalle de Órdenes
            </CardTitle>
            {/* Search Input */}
            <div className="relative print:hidden">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar estudio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full md:w-72"
              />
            </div>
          </div>
          {/* Study Statistics when searching */}
          {studyStats && studyStats.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg print:hidden">
              <p className="text-sm font-medium text-blue-800 mb-2">Estudios encontrados:</p>
              <div className="flex flex-wrap gap-2">
                {studyStats.map((stat, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-800">
                    {stat.name}: {stat.patientCount} paciente{stat.patientCount !== 1 ? 's' : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="print:pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {searchTerm ? (
                <>
                  <p className="text-lg">No se encontraron estudios</p>
                  <p className="text-sm">Intente con otro término de búsqueda</p>
                </>
              ) : (
                <>
                  <p className="text-lg">No hay órdenes para esta fecha</p>
                  <p className="text-sm">Seleccione otra fecha o registre nuevos pacientes</p>
                </>
              )}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[60px] print:w-[40px] print:text-xs font-semibold">No.</TableHead>
                    <TableHead className="w-[100px] print:w-[80px] print:text-xs font-semibold">Folio</TableHead>
                    <TableHead className="w-[200px] print:w-[150px] print:text-xs font-semibold">Paciente</TableHead>
                    <TableHead className="print:text-xs font-semibold">Estudios</TableHead>
                    <TableHead className="w-[100px] print:w-[70px] print:text-xs text-center font-semibold">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order, index) => (
                    <TableRow key={order.id} className="print:text-xs">
                      <TableCell className="font-medium text-center print:py-1">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-mono text-sm print:text-xs print:py-1">
                        {order.patient_folio || order.order_number || '-'}
                      </TableCell>
                      <TableCell className="print:py-1">
                        <div>
                          <p className="font-medium print:text-xs">{order.patient_name || 'Sin nombre'}</p>
                          {order.doctor_name && (
                            <p className="text-xs text-muted-foreground">
                              Dr: {order.doctor_name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="print:py-1">
                        <div className="space-y-1">
                          {order.studies?.map((study, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{study.study_code || '-'}</span>
                              <span className="text-sm print:text-xs">{study.study_name}</span>
                            </div>
                          ))}
                          {(!order.studies || order.studies.length === 0) && (
                            <span className="text-muted-foreground text-sm">Sin estudios</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center print:py-1">
                        <Badge 
                          variant={
                            order.status === 'completed' ? 'default' :
                            order.status === 'in_progress' ? 'secondary' : 'outline'
                          }
                          className="print:text-[10px] print:px-1 print:py-0"
                        >
                          {order.status === 'completed' ? 'Completado' :
                           order.status === 'in_progress' ? 'En Proceso' : 'Pendiente'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print footer */}
      <div className="hidden print:block text-center text-xs text-gray-500 mt-4">
        <p>Generado el {new Date().toLocaleString('es-MX')}</p>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 1cm; }
        }
      `}</style>
    </div>
  );
}
